import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import styled from "styled-components";
import usePutCustomerInfo from "../../hooks/usePutCustomerInfo";
import theme from "../../theme";
import { Customer, SystemSetting } from "../../types/types";
import InputField from "./inputField";
import AlertModal from "../alertModal";
import { WarningIcon } from "../../assets/icons/icons";
import { Button } from "../common/Button";
import { ModalLayout } from "../common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../../locales";

const { md } = theme.screen;

export interface InfoType {
  seq: number;
  id: string;
  pw: string | null;
  name: string;
  phone: string;
  address: string;
  address_detail: string;
  group_id: string;
  registered_at: string;
  // sex: any;
  // birth: any;
  illness: string;
}

const CustomerModifyModal: React.FC<{
  open: boolean;
  onPressNo: () => void;
  seq: number;
  data?: Customer;
  setDetailData: any;
  systemSetting: SystemSetting | null;
}> = (props) => {
  const { open, onPressNo, data, setDetailData, systemSetting } = props;
  const { width } = useWindowSize();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const [pwError, setPwError] = useState("");
  const [checkPwSpace, setCheckPwSpace] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [info, setInfo] = useState<InfoType>({
    seq: 0,
    id: "",
    pw: "",
    name: "",
    phone: "",
    address: "",
    address_detail: "",
    group_id: "00000",
    registered_at: "",
    // sex: "",
    // birth: "",
    illness: "",
  });
  const [errors, setErrors] = useState({
    birthError: false,
    genderError: false,
    idError: false,
  });
  const queryClient = useQueryClient();

  const body = {
    seq: info.seq,
    id: info.id,
    pw: info.pw /*? SHA256( info.pw ).toString() : null*/, // 비밀번호 수정 안한경우 null 전달
    name: info.name,
    phone: info.phone,
    address: info.address,
    address_detail: info.address_detail ? info.address_detail : "",
    group_id: info.group_id,
    registered_at: info.registered_at,
    // sex: info.sex,
    // birth: info.birth,
    illness: info.illness,
  };

  const { mutate: patchCustomerInfo, isPending } = usePutCustomerInfo();

  const handlePatch = () => {
    patchCustomerInfo(body, {
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["Customer"],
        });
        onPressNo();
        enqueueSnackbar(data.message, {
          variant: "success",
        });
        setDetailData(body);
        return data;
      },
    });
  };

  // 필수 입력 조건
  const isAllFilled =
    pwError === "" &&
    [info.name, info.id, info.address].every((el) => el !== "") &&
    info.id.length >= 10 &&
    !errors.birthError &&
    !errors.genderError &&
    !errors.idError;

  const handleClose = () => {
    setOpenConfirmModal(false);
    onPressNo();
    // setSelected(null);
    setInfo({
      seq: 0,
      id: "",
      pw: "",
      name: "",
      phone: "",
      address: "",
      address_detail: "",
      group_id: "00000",
      registered_at: "",
      // sex: "",
      // birth: "",
      illness: "",
    });
    setPwError("");
  };

  // console.log("수정모달", data);
  // console.log(isAllFilled);

  return (
    <ModalLayout
      open={open}
      onClose={() => setOpenConfirmModal(true)}
      width="520px"
      title={t.manageCustomer.modifyInfoTitle}
    >
      <InputField
        info={info}
        setInfo={setInfo}
        data={data}
        pwError={pwError}
        setPwError={setPwError}
        checkPwSpace={checkPwSpace}
        setCheckPwSpace={setCheckPwSpace}
        errors={errors}
        setErrors={setErrors}
      />
      <BtnWrapper>
        <Button
          onClick={() => {
            if (checkPwSpace || pwError) {
              enqueueSnackbar(t.manageCustomer.invalidPassword, {
                variant: "error",
              });
              return;
            } else {
              handlePatch();
            }
          }}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
          isDisabled={!isAllFilled || !systemSetting?.update}
          isLoading={isPending}
        >
          {width <= md ? t.common.save : t.manageCustomer.modifyComplete}
        </Button>
        <Button
          bgColor={theme.statusColor.CLOSE}
          onClick={() => setOpenConfirmModal(true)}
          hoverColor={theme.statusColor.CLOSE_HOVER}
        >
          {t.common.cancel}
        </Button>
      </BtnWrapper>
      <AlertModal
        open={openConfirmModal}
        onPressNo={() => setOpenConfirmModal(false)}
        title={t.manageCustomer.cancelModifyTitle}
        content={t.manageCustomer.cancelModifyContent}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
        onPressYes={handleClose}
      />
    </ModalLayout>
  );
};

const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;
  }
`;

export default CustomerModifyModal;
